import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, FileText, User, Mail, Calendar, Hash, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';

const DOCUMENT_TYPES_LABELS = {
  bulletin_adhesion: "Bulletin d'Adhésion",
  conditions_generales: 'Conditions Générales',
  convention_mise_disposition: 'Convention de Mise à Disposition',
  fiche_bilan_mission: 'Fiche de Bilan de Mission',
};

export default function ContractVerification() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [manualId, setManualId] = useState('');

  useEffect(() => {
    if (documentId) {
      verifyDocument(documentId);
    } else {
      setLoading(false);
    }
  }, [documentId]);

  const verifyDocument = async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/contracts/verify/${id}`
      );

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Document non trouvé');
        }
        throw new Error('Erreur lors de la vérification');
      }

      const data = await response.json();
      setDocument(data);
      toast.success('Document vérifié avec succès');
    } catch (err) {
      setError(err.message);
      setDocument(null);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleManualVerification = (e) => {
    e.preventDefault();
    if (manualId.trim()) {
      navigate(`/verify-document/${manualId.trim()}`);
      verifyDocument(manualId.trim());
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Vérification en cours...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Vérification de Document
          </h1>
          <p className="text-gray-600">
            Vérifiez l'authenticité d'un document signé électroniquement
          </p>
        </div>

        {/* Formulaire de vérification manuelle */}
        {!documentId && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Entrer un ID de document</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleManualVerification} className="flex gap-2">
                <input
                  type="text"
                  value={manualId}
                  onChange={(e) => setManualId(e.target.value)}
                  placeholder="ID du document (ex: 550e8400-e29b-41d4-a716-446655440000)"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  Vérifier
                </Button>
              </form>
              <p className="text-sm text-gray-500 mt-2">
                Vous pouvez trouver l'ID du document dans l'URL du QR code ou en bas du document PDF
              </p>
            </CardContent>
          </Card>
        )}

        {/* Résultat de la vérification */}
        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="py-4 md:py-8">
              <div className="text-center">
                <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-red-900 mb-2">
                  Document non valide
                </h2>
                <p className="text-red-700 mb-4">{error}</p>
                <p className="text-sm text-red-600">
                  Ce document n'a pas pu être vérifié. Il peut être falsifié ou l'ID peut être incorrect.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {document && (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="py-4 md:py-8">
              <div className="text-center mb-6">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-green-900 mb-2">
                  ✓ Document authentique et valide
                </h2>
                <p className="text-green-700">
                  Ce document a été vérifié et est authentique
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <div className="text-sm text-gray-500">Type de document</div>
                      <div className="font-medium text-gray-900">
                        {DOCUMENT_TYPES_LABELS[document.document_type] || document.document_type}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Hash className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <div className="text-sm text-gray-500">Référence</div>
                      <div className="font-medium text-gray-900">{document.reference}</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <User className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <div className="text-sm text-gray-500">Signataire</div>
                      <div className="font-medium text-gray-900">{document.signer_name}</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <div className="text-sm text-gray-500">Email</div>
                      <div className="font-medium text-gray-900">{document.signer_email}</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 md:col-span-2">
                    <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <div className="text-sm text-gray-500">Date et heure de signature</div>
                      <div className="font-medium text-gray-900">
                        {new Date(document.signed_at).toLocaleDateString('fr-FR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                          timeZoneName: 'short',
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <div className="text-sm text-gray-500 mb-1">ID du document</div>
                  <div className="font-mono text-xs text-gray-700 break-all bg-gray-50 p-2 rounded">
                    {document.document_id}
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-900">
                  <strong>Garantie d'authenticité :</strong> Ce document a été signé électroniquement 
                  et enregistré de manière sécurisée. Les informations ci-dessus correspondent exactement 
                  au document original signé par {document.signer_name}.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Instructions */}
        {!document && !error && (
          <Card>
            <CardHeader>
              <CardTitle>Comment vérifier un document ?</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 mb-1">Scanner le QR code</h3>
                    <p className="text-gray-600 text-sm">
                      Utilisez votre smartphone pour scanner le QR code présent sur le document PDF
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 mb-1">Ou entrer l'ID manuellement</h3>
                    <p className="text-gray-600 text-sm">
                      Copiez l'ID du document (présent en bas de page) et collez-le dans le champ ci-dessus
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 mb-1">Vérification instantanée</h3>
                    <p className="text-gray-600 text-sm">
                      Le système vérifie immédiatement l'authenticité et affiche les détails du document
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
